import { useCallback, useEffect, useRef, useState } from "react";
import { elementInViewport } from "@bloomberg/media-ui-core";

export function useIntersectionObserverTracker<T extends HTMLElement>(
    onEnter: () => void,
    onLeave: () => void,
    threshold = 0
) {
    const [el, setEl] = useState<T>();
    const viewportState = useRef(new WeakMap());
    const ref = useCallback((node: T) => setEl(node), []);

    useEffect(() => {
        if (!el) return undefined;

        viewportState.current.set(el, undefined);

        const observer = setupIntersectionObserver(
            ([entry]) => {
                if (entry.intersectionRatio <= 0) return;

                const inViewport = elementInViewport(el, threshold);

                const wasVisible = viewportState.current.get(el);
                const viewportStatusChanged = inViewport !== wasVisible;

                viewportState.current.set(el, inViewport);

                if (!viewportStatusChanged) {
                    return;
                }

                if (inViewport) {
                    onEnter();

                    return;
                }

                onLeave();
            },
            el,
            {
                threshold,
            }
        );

        return () => {
            observer.disconnect();
        };
    }, [el]);

    return ref;
}

function setupIntersectionObserver(
    fn: IntersectionObserverCallback,
    target: HTMLElement,
    options?: IntersectionObserverInit
) {
    const observer = new IntersectionObserver(fn, options);
    observer.observe(target);

    return observer;
}
